import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ColumnManagerDialogComponent } from '../../components/column-manager/column-manager-dialog/column-manager-dialog.component';
import { ColumnConfig } from '../../components/column-manager/column-manager.component';
import { ColumnDefinitionBase, ColumnDefinition } from '../../models/column-definition';
import { MatSort } from '@angular/material/sort';
export interface TableConfiguration {
  tableId: string;
  columns: ColumnDefinitionBase[];
  displayedColumns: (string | undefined)[];
}

@Injectable({
  providedIn: 'root'
})
export class ColumnManagerService {

  private tableConfigurations: Map<string, TableConfiguration> = new Map();
  private defaultColumns: ColumnDefinitionBase[] = [];
  private readonly isBrowser: boolean;

  constructor(private dialog: MatDialog, @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  registerTable(tableId: string, columns: ColumnDefinitionBase[]): void {
    const displayedColumns = columns
      .filter(col => col.visible)
      .map(col => col.columnDef);
    this.tableConfigurations.set(tableId, {
      tableId,
      columns: [...columns],
      displayedColumns
    });

    this.defaultColumns = [...columns];
  }

  getTableConfiguration(tableId: string): TableConfiguration | undefined {
    return this.tableConfigurations.get(tableId);
  }

  getDisplayedColumns(tableId: string): (string | undefined)[] {
    const config = this.tableConfigurations.get(tableId);
    return config ? config.displayedColumns : [];
  }

  updateTableConfig(tableId: string, config: ColumnConfig): void {
    const existingConfig = this.tableConfigurations.get(tableId);
    if (existingConfig) {
      this.tableConfigurations.set(tableId, {
        ...existingConfig,
        columns: config.columns,
        displayedColumns: config.displayedColumns
      });
    }
  }

  openColumnManager(tableId: string, options?: {
    width?: string;
    height?: string;
  }): Observable<ColumnConfig | undefined> {
    const config = this.tableConfigurations.get(tableId);
    if (!config) {
      throw new Error(`Table configuration not found for tableId: ${tableId}`);
    }

    const dialogRef = this.dialog.open(ColumnManagerDialogComponent, {
      width: options?.width || '450px',
      height: options?.height || 'auto',
      data: {
        tableId,
        columns: [...config.columns],
        defaultColumns: [...this.defaultColumns]
      }
    });

    return dialogRef.afterClosed();
  }

  resetTable(tableId: string): void {
    const config = this.tableConfigurations.get(tableId);
    if (config) {
      const resetColumns = config.columns.map(col => ({
        ...col,
        visible: col?.required || col?.visible
      }));

      this.updateTableConfig(tableId, {
        columns: resetColumns as unknown as ColumnDefinition[],
        displayedColumns: resetColumns.filter(col => col.visible).map(col => col.key)
      });
    }
  }
  saveToStorage(tableId: string): void {
    if (!this.isBrowser) {
      return;
    }

    const config = this.tableConfigurations.get(tableId);
    if (config) {
      localStorage.setItem(`table-config-${tableId}`, JSON.stringify(config));
    }
  }

  loadFromStorage(tableId: string): boolean {
    if (!this.isBrowser) {
      return false;
    }

    try {
      const stored = localStorage.getItem(`table-config-${tableId}`);
      if (stored) {
        const storedConfig = JSON.parse(stored) as TableConfiguration;
        const currentConfig = this.tableConfigurations.get(tableId);

        if (currentConfig) {
          this.mergeStoredWithCurrentConfig(tableId, storedConfig, currentConfig);
        } else {
          this.tableConfigurations.set(tableId, storedConfig);
        }
        return true;
      }
    } catch (error) {
      console.warn(`Failed to load table configuration for ${tableId}:`, error);
    }
    return false;
  }

  private mergeStoredWithCurrentConfig(
    tableId: string,
    storedConfig: TableConfiguration,
    currentConfig: TableConfiguration
  ): void {
    // If an old column set is stored in local storage, update with the current configuration to include any new columns and remove obsolete ones.
    const storedColumnDefs = new Set(storedConfig.columns.map(c => c.columnDef));
    const currentColumnDefs = new Set(currentConfig.columns.map(c => c.columnDef));

    const newColumns = currentConfig.columns.filter(c => !storedColumnDefs.has(c.columnDef));
    const validStoredColumns = storedConfig.columns.filter(c => currentColumnDefs.has(c.columnDef));
    const mergedColumns = [...validStoredColumns, ...newColumns];

    const mergedDisplayedColumns = mergedColumns
      .filter(col => col.visible)
      .map(col => col.columnDef);

    const mergedConfig: TableConfiguration = {
      tableId,
      columns: mergedColumns,
      displayedColumns: mergedDisplayedColumns
    };

    this.tableConfigurations.set(tableId, mergedConfig);

    const columnsChanged = newColumns.length > 0 || validStoredColumns.length !== storedConfig.columns.length;
    if (columnsChanged) {
      this.saveToStorage(tableId);
    }
  }

  isColumnVisible(tableId: string, columnKey: string): boolean {
    const config = this.tableConfigurations.get(tableId);
    return config ? config.displayedColumns.includes(columnKey) : false;
  }

  saveSortState(tableId: string, sort: MatSort): void {
    if (!this.isBrowser) {
      return;
    }

    if (!sort.active || !sort.direction) return;

    const sortState = {
      active: sort.active,
      direction: sort.direction
    };

    localStorage.setItem(`${tableId}_sort`, JSON.stringify(sortState));
  }

  loadSortState(tableId: string): { active: string; direction: 'asc' | 'desc' } | null {
    if (!this.isBrowser) {
      return null;
    }

    const stored = localStorage.getItem(`${tableId}_sort`);
    return stored ? JSON.parse(stored) : null;
  }

  clearSortState(tableId: string): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem(`${tableId}_sort`);
  }

  savePageSize(tableId: string, pageSize: number): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.setItem(`${tableId}_pageSize`, pageSize.toString());
  }

  loadPageSize(tableId: string): number | null {
    if (!this.isBrowser) {
      return null;
    }

    const stored = localStorage.getItem(`${tableId}_pageSize`);
    return stored ? parseInt(stored, 10) : null;
  }

  clearPageSize(tableId: string): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem(`${tableId}_pageSize`);
  }
}